import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DownOutlined } from "@ant-design/icons";
import { orgTourInfoContentLooksLikeRawSource } from "../../../utils/orgTourInfoDisplay";
import { stripSupplierHtmlToReadableText } from "../../../utils/resolveOrganizedTourItineraryFallback";
import { resolveOrganizedTourItineraryContent } from "../../../utils/resolveOrganizedTourItineraryContent";
import { OrganizedTourItineraryContentRenderer } from "./OrganizedTourItineraryContentRenderer";
import { sanitizeOrganizedTourQuoteText } from "../utils/sanitizeOrganizedTourQuoteText";
import "./OrganizedTourItineraryNarrativeAccordion.css";

const TITLE_HE = "פירוט מסלול הטיול";

/**
 * טקסט מסלול בלבד (ללא HTML מזוהה כמקור גולמי). HTML מסונן מוצג כטקסט קריא אחרי הסרת תגים.
 */
export function resolveOrganizedTourItineraryPlainBody(narrativeText, narrativeHtml) {
  const t = typeof narrativeText === "string" ? narrativeText.trim() : "";
  if (t) {
    if (orgTourInfoContentLooksLikeRawSource(t)) return "";
    return sanitizeOrganizedTourQuoteText(t) || t;
  }
  const h = typeof narrativeHtml === "string" ? narrativeHtml.trim() : "";
  if (!h || orgTourInfoContentLooksLikeRawSource(h)) return "";
  const stripped = stripSupplierHtmlToReadableText(h);
  const cleaned = sanitizeOrganizedTourQuoteText(stripped.trim());
  return (cleaned || stripped).trim();
}

function buildPlainNarrative(narrativeText, narrativeHtml) {
  return resolveOrganizedTourItineraryPlainBody(narrativeText, narrativeHtml);
}

/**
 * Accordion צר לפירוט מסלול — סגור כברירת מחדל, תקציר 2–3 שורות כשסגור.
 *
 * @param {{
 *   narrativeText?: string|null,
 *   narrativeHtml?: string|null,
 *   resourceUrl?: string|null,
 *   instanceId?: string,
 *   onOpenChange?: (open: boolean) => void,
 * }} props
 */
export function OrganizedTourItineraryNarrativeAccordion({
  narrativeText,
  narrativeHtml,
  resourceUrl,
  resourceKind,
  instanceId = "default",
  onOpenChange,
}) {
  const [open, setOpen] = useState(false);

  const sourcePath = `profile.accordion.${instanceId}`;

  const renderModel = useMemo(
    () =>
      resolveOrganizedTourItineraryContent({
        contentText: narrativeText ?? "",
        contentHtmlSanitized: narrativeHtml ?? "",
        resourceUrl,
        resourceKind,
        sourcePath,
      }),
    [narrativeText, narrativeHtml, resourceUrl, resourceKind, sourcePath]
  );

  const plain = useMemo(
    () => buildPlainNarrative(narrativeText ?? "", narrativeHtml ?? ""),
    [narrativeText, narrativeHtml]
  );

  const hasBody = renderModel.renderMode !== "none";

  useEffect(() => {
    if (!hasBody) return;
    // eslint-disable-next-line no-console
    console.info("[ORG_TOUR_ITINERARY_ACCORDION_RENDER]", {
      instanceId,
      charCount: plain.length,
      renderMode: renderModel.renderMode,
    });
  }, [instanceId, plain.length, hasBody, renderModel.renderMode]);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      // eslint-disable-next-line no-console
      console.info("[ORG_TOUR_ITINERARY_ACCORDION_TOGGLE]", {
        instanceId,
        open: next,
      });
      onOpenChange?.(next);
      return next;
    });
  }, [instanceId, onOpenChange]);

  if (!hasBody) return null;

  return (
    <div className="ot-itin-acc" dir="rtl" lang="he">
      <button
        type="button"
        className="ot-itin-acc__header"
        aria-expanded={open}
        aria-controls={`ot-itin-acc-panel-${instanceId}`}
        id={`ot-itin-acc-trigger-${instanceId}`}
        onClick={toggle}
      >
        <span className="ot-itin-acc__title">{TITLE_HE}</span>
        <span className={`ot-itin-acc__chevron${open ? " ot-itin-acc__chevron--open" : ""}`} aria-hidden>
          <DownOutlined className="ot-itin-acc__chevron-icon" />
        </span>
      </button>
      <div
        className={`ot-itin-acc__panel${open ? " ot-itin-acc__panel--expanded" : ""}`}
        id={`ot-itin-acc-panel-${instanceId}`}
        role="region"
        aria-labelledby={`ot-itin-acc-trigger-${instanceId}`}
      >
        <OrganizedTourItineraryContentRenderer
          contentText={narrativeText}
          contentHtmlSanitized={narrativeHtml}
          resourceUrl={resourceUrl}
          resourceKind={resourceKind}
          sourcePath={sourcePath}
          compact={!open && renderModel.renderMode === "text_plain"}
        />
      </div>
    </div>
  );
}
